import type {
  CompareMode,
  ExploreScopeSelection,
  PeriodGrain,
  PeriodPreset,
  PeriodSpec,
} from "@/analytics/core/types";
import { parseScopeSelection, writeScopeSelection } from "@/analytics/core/scope-params";
import { resolveDefaultCompare, resolvePeriodWindow } from "@/analytics/core/period-windows";
import { normalizeExploreMetricId } from "@/grid/config/catalog";
import type { DepartmentDefinition } from "@/grid/core/types";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export interface ExploreParams {
  periodSpec: PeriodSpec;
  compareMode: CompareMode;
  selection: ExploreScopeSelection;
  metricId: string;
}

const PRESETS: PeriodPreset[] = [
  "this_week",
  "last_week",
  "this_month",
  "last_month",
  "this_quarter",
  "last_quarter",
  "this_year",
];

const GRAINS: PeriodGrain[] = ["week", "month", "quarter"];

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const MONTHLY_DEFAULT_METRICS = new Set(["opps", "sales"]);

function defaultPeriodPreset(metricId: string): PeriodPreset {
  return MONTHLY_DEFAULT_METRICS.has(metricId) ? "this_month" : "this_week";
}

function parsePreset(value: string | null, metricId: string): PeriodPreset {
  if (value && PRESETS.includes(value as PeriodPreset)) return value as PeriodPreset;
  return defaultPeriodPreset(metricId);
}

function parseGrain(value: string | null): PeriodGrain {
  if (value && GRAINS.includes(value as PeriodGrain)) return value as PeriodGrain;
  return "week";
}

function parsePeriodSpec(searchParams: URLSearchParams, metricId: string): PeriodSpec {
  const presetParam = searchParams.get("preset");
  if (presetParam === "custom") {
    const start = searchParams.get("start");
    if (start && ISO_DATE_RE.test(start)) {
      return {
        preset: "custom",
        grain: parseGrain(searchParams.get("grain")),
        customStart: start,
      };
    }
    return { preset: defaultPeriodPreset(metricId) };
  }
  return { preset: parsePreset(presetParam, metricId) };
}

function parseCompareMode(value: string | null): CompareMode {
  return value === "prev_year" ? "prev_year" : "prev_period";
}

function writePeriodSpec(next: URLSearchParams, spec: PeriodSpec): void {
  if (spec.preset === "custom" && spec.customStart) {
    next.set("preset", "custom");
    next.set("grain", spec.grain ?? "week");
    next.set("start", spec.customStart);
    return;
  }
  next.set("preset", spec.preset);
  next.delete("grain");
  next.delete("start");
}

function writeCompareMode(next: URLSearchParams, mode: CompareMode): void {
  if (mode === "prev_year") {
    next.set("compare", "prev_year");
  } else {
    next.delete("compare");
  }
}

export function useExploreParams(defaultMetricId: string, department?: DepartmentDefinition) {
  const [searchParams, setSearchParams] = useSearchParams();

  const params: ExploreParams = useMemo(() => {
    const rawMetricId = searchParams.get("metric") ?? defaultMetricId;
    const metricId = department ? normalizeExploreMetricId(department, rawMetricId) : rawMetricId;

    return {
      periodSpec: parsePeriodSpec(searchParams, metricId),
      compareMode: parseCompareMode(searchParams.get("compare")),
      selection: parseScopeSelection(searchParams),
      metricId,
    };
  }, [searchParams, defaultMetricId, department]);

  const period = useMemo(() => resolvePeriodWindow(params.periodSpec), [params.periodSpec]);
  const compare = useMemo(
    () => resolveDefaultCompare(period, params.compareMode),
    [period, params.compareMode],
  );

  const updateParams = useCallback(
    (patch: {
      preset?: PeriodPreset;
      periodSpec?: PeriodSpec;
      compareMode?: CompareMode;
      selection?: ExploreScopeSelection;
      metricId?: string;
    }) => {
      setSearchParams((current) => {
        const next = new URLSearchParams(current);

        if (patch.periodSpec) writePeriodSpec(next, patch.periodSpec);
        else if (patch.preset) writePeriodSpec(next, { preset: patch.preset });

        if (patch.compareMode) writeCompareMode(next, patch.compareMode);
        if (patch.metricId) next.set("metric", patch.metricId);

        if (patch.selection) writeScopeSelection(next, patch.selection);

        return next;
      });
    },
    [setSearchParams],
  );

  const setTeams = useCallback(
    (teamIds: number[]) => {
      updateParams({
        selection: {
          teamIds,
          repIds: teamIds.length === 1 ? params.selection.repIds : [],
        },
      });
    },
    [params.selection.repIds, updateParams],
  );

  const setReps = useCallback(
    (repIds: number[]) => {
      updateParams({
        selection: {
          teamIds: params.selection.teamIds,
          repIds,
        },
      });
    },
    [params.selection.teamIds, updateParams],
  );

  return { params, period, compare, updateParams, setTeams, setReps };
}
